import { apiClient } from "@/api"
import { format } from "date-fns"
import { DEFAULTS, ENDPOINTS } from "../constants"

/**
 * Safely converts a value to an integer.
 */
export const safeParseInt = value => {
  if (!value) return null
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? null : parsed
}

/**
 * Extracts filter parameters from URLSearchParams and applies defaults.
 */
export const getInitialFilters = searchParams => ({
  date: searchParams.get("date") || format(new Date(), DEFAULTS.DATE_FORMAT),
  tutor_id: safeParseInt(searchParams.get("tutor_id")),
  school_id: safeParseInt(searchParams.get("school_id")),
  group_id: safeParseInt(searchParams.get("group_id")),
  type: searchParams.get("type") || DEFAULTS.SESSION_TYPE,
  view: searchParams.get("view") || DEFAULTS.VIEW_MODE,
})

/**
 * Maps initial filter IDs to autocomplete option objects.
 */
export const getDefaultValues = ({ tutor_id, school_id, group_id, type, view, date }) => ({
  tutor: tutor_id ? { value: tutor_id } : null,
  school: school_id ? { value: school_id } : null,
  group: group_id ? { value: group_id } : null,
  type: type || "group_sessions",
  view: view || "week",
  date: date || null,
})

/**
 * Validates filters based on app rules.
 */
export const validateFilters = ({ group_id, school_id, tutor_id, view }) => {
  if (group_id && !school_id) {
    return "School must be selected when filtering by Group"
  }
  if (view === "month" && !tutor_id && !school_id) {
    return "To view sessions by month, please select a tutor or school below"
  }
  return null
}

/**
 * Updates URL parameters based on selected filter values after validation.
 */
export const updateFiltersAndURL = (selectedValues, setValidationError, updateUrlParams) => {
  const filters = {
    tutor_id: selectedValues.tutor?.value || null,
    school_id: selectedValues.school?.value || null,
    group_id: selectedValues.group?.value || null,
    type: selectedValues.type || "group_sessions",
    view: selectedValues.view || "week",
  }

  if (selectedValues.date) {
    filters.date = selectedValues.date
  }

  const error = validateFilters(filters)
  if (error) {
    setValidationError(error)
    return false
  }

  setValidationError(null)
  updateUrlParams(filters)
}

/**
 * Fetches the initial value from an API endpoint.
 */
export const getInitialValue = async (endpoint, id, additionalParams = {}) => {
  try {
    const params = { id, ...additionalParams }
    const { data } = await apiClient.get(ENDPOINTS.autocomplete[endpoint], { params })

    if (!data) {
      console.error(`No data returned for ${endpoint} with ID ${id}`)
      return null
    }
    if (data.items && Array.isArray(data.items)) {
      return data.items.length ? data.items[0] : null
    }
    if (data && typeof data === "object" && "value" in data && "title" in data) {
      return data
    }
    console.warn(`Unexpected data format for ${endpoint}:`, data)
    return data
  } catch (error) {
    console.error(`Failed to fetch initial value for ${endpoint} with ID ${id}:`, error)
    return null
  }
}

/**
 * Fetches initial values for a field or multiple fields.
 */
export const fetchInitialValues = async (endpoint, field, id, contextData = {}) => {
  // Consolidated helper: applies additional params conditionally.
  const fetchValue = async (ep, fieldKey) => {
    const additionalParams = ep === "groups" && contextData.school_id ? { school_id: contextData.school_id } : {}
    const value = await getInitialValue(ep, id, additionalParams)
    return value ? { type: fieldKey.replace("_id", ""), value } : null
  }

  if (typeof endpoint === "string") {
    return await fetchValue(endpoint, field)
  }

  if (typeof endpoint === "object") {
    const results = {}
    for (const [fieldKey, ep] of Object.entries(endpoint)) {
      const result = await fetchValue(ep, fieldKey)
      if (result) {
        results[fieldKey.replace("_id", "")] = result.value
      }
    }
    return results
  }
  return null
}

/**
 * Returns the autocomplete endpoint URL for a given field.
 */
export const getAutoCompleteEndpoint = field => {
  const endpoints = {
    tutors: "/api/admin/autocomplete/tutors",
    schools: "/api/admin/autocomplete/schools",
    groups: "/api/autocomplete/educatables",
  }
  return endpoints[field]
}
